import { useCallback, useState } from "react";
import { type ApiError, OpenAPI } from "../services/intranet-portal-services";

import { selectToken } from "../views/utilities/auth/authSlice";
import { runTimeConfig } from "@/config";
import { isApiError, showApiError } from "@/utils/apiErrorHandler";

export interface UseIntranetPortalCoreApiReturn {
  handleRequest: <T>(requestFn: () => Promise<T>) => Promise<T | undefined>;
  isLoading: boolean;
  error: ApiError | undefined;
  dismissError: () => void;
}

const useIntranetPortalCoreApi = (): UseIntranetPortalCoreApiReturn => {
  const [error, setError] = useState<ApiError | undefined>(undefined);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  // Ensure BASE is set (fallback to empty string if missing)
  const baseUrl = runTimeConfig?.VITE_BASE_URL ?? "";
  if (OpenAPI.BASE !== baseUrl) {
    OpenAPI.BASE = baseUrl;
  }

  // Use a resolver function so every request gets the latest token from localStorage
  OpenAPI.TOKEN = async () => {
    const token = selectToken();
    return typeof token === "string" ? token : "";
  };

  const handleRequest = useCallback(
    async <T,>(requestFn: () => Promise<T>): Promise<T | undefined> => {
      setIsLoading(true);

      try {
        const response = await requestFn();
        return response;
      } catch (exception: unknown) {
        showApiError(exception);
        setError(isApiError(exception) ? exception : undefined);
        return undefined;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  return {
    handleRequest,
    isLoading,
    error,
    dismissError: () => {
      setError(undefined);
    }
  };
};

export default useIntranetPortalCoreApi;
